/** @format */

import { BehaviorSubject, Observable } from 'rxjs';
import {
  CertonaRecommendations,
  CertonaRecommendationsResponse,
  CertonaScheme,
} from '@shared/data-access/interfaces';
import { Injectable, NgZone, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { Logger, WINDOW } from '@shared/data-access/services';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { SchemeStatus } from '../interfaces/scheme-status.interface';
import { isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class CertonaService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly window = inject(WINDOW);
  private readonly logger = inject(Logger);

  private monetateRecommendationsHandler?: (certonaRecommendations: CertonaRecommendations) => void;

  // Keep existing subject for backward compatibility
  private readonly certonaRecommendationsSubject = new BehaviorSubject<
    CertonaRecommendationsResponse | undefined | null
  >(undefined);

  public certonaRecommendationsState = this.certonaRecommendationsSubject.asObservable();

  // New signal-based state for schemes
  private readonly schemesSignal = signal<Record<string, CertonaScheme>>({});

  /**
   * Have we received any recommendations data at all?
   * Monetate excludes schemes instead of sending empty ones, so we need this to distinguish between
   * "waiting for data" and "data does not include this scheme"
   */
  private readonly isInitialized = signal(false);

  private readonly resonanceReadySubject = new BehaviorSubject<
    { isPLAEnabled: boolean } | undefined
  >(undefined);

  public readonly resonanceReadyState = this.resonanceReadySubject.asObservable();
  private readonly cartCertonaReadySubject = new BehaviorSubject<any>(undefined);
  public readonly cartCertonaReadyState = this.cartCertonaReadySubject.asObservable();

  private readonly _showLoadingIndicators = signal(true);
  public readonly showLoadingIndicators = this._showLoadingIndicators.asReadonly();

  private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly startTime = Date.now();
  private readonly endTime = signal<number | null>(null);
  private readonly logTimeComponentName = signal<string | null>(null);
  private logTimeEcode: string | null = null;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        this.loadingTimeoutId = setTimeout(() => {
          this.ngZone.run(() => {
            this._showLoadingIndicators.set(false);
          });
        }, DCSG_CONSTANTS.productRecs.timeout);
      });
    }

    effect(() => {
      if (this.isInitialized()) {
        if (this.loadingTimeoutId) {
          clearTimeout(this.loadingTimeoutId);
          this.loadingTimeoutId = null;
        }
        this._showLoadingIndicators.set(false);
      }
    });

    effect(() => {
      if (this.isInitialized() && !this.endTime()) {
        this.endTime.set(Date.now());
      }
    });

    effect(() => {
      if (this.endTime() && this.logTimeComponentName()) {
        const timeToData = Number(this.endTime()) - this.startTime;
        this.logger.log('Monetate Schema Loaded', {
          loadTimeMs: timeToData,
          component: this.logTimeComponentName(),
          ecode: this.logTimeEcode,
        });
      }
    });
  }

  logLoadingTime(componentName: string, ecode: string) {
    this.logTimeComponentName.set(componentName);
    this.logTimeEcode = ecode;
  }

  /**
   * Get a specific scheme by name
   * @param schemeName The name of the scheme to retrieve
   * @returns A computed signal containing the scheme or null if not found
   */
  getScheme(schemeName: string) {
    return computed(() => this.schemesSignal()[schemeName] || null);
  }

  /**
   * Check if a scheme exists and has displayable content
   * @param schemeName The name of the scheme to check
   * @returns A computed signal that's true when the scheme exists with display: 'yes' and items
   */
  getSchemeStatus(schemeName: string) {
    return computed((): SchemeStatus => {
      const isInitialized = this.isInitialized();
      const scheme = isInitialized ? this.getScheme(schemeName)() : undefined;
      const isValid = !!scheme && Array.isArray(scheme.items) && scheme.items.length > 0;
      const isDisplayable = isValid && scheme.display === 'yes';
      return {
        initialized: isInitialized,
        valid: isValid,
        displayable: isDisplayable,
      };
    });
  }

  // Observable version for backward compatibility
  getScheme$(schemeName: string): Observable<CertonaScheme | null> {
    const schemeSignal = this.getScheme(schemeName);
    return toObservable(schemeSignal);
  }

  // Observable version for backward compatibility
  getSchemeStatus$(schemeName: string): Observable<SchemeStatus> {
    const hasValidSignal = this.getSchemeStatus(schemeName);
    return toObservable(hasValidSignal);
  }

  resonanceReady(isPLAEnabled) {
    this.resonanceReadySubject.next({
      isPLAEnabled,
    });
  }

  cartCertona(isCartCertona) {
    this.cartCertonaReadySubject.next({
      isCartCertona,
    });
  }

  /**
   * Set recommendations data - maintains original signature for backward compatibility
   * @param certonaRecommendations The recommendations data
   */
  recommendations(certonaRecommendations: CertonaRecommendations) {
    this.certonaRecommendationsSubject.next({
      certonaRecommendations,
    });

    // Update signal-based state with new schemes (safely handle any data structure)
    if (
      certonaRecommendations?.resonance?.schemes &&
      Array.isArray(certonaRecommendations.resonance.schemes)
    ) {
      // Create completely new schemes object to ensure signal reactivity
      const newSchemes: Record<string, CertonaScheme> = {
        ...this.schemesSignal(),
      };

      // Process each scheme in the recommendations
      for (const scheme of certonaRecommendations.resonance.schemes) {
        // Store scheme by its name for easy lookup with deep copy
        if (scheme?.scheme) {
          newSchemes[scheme.scheme] = { ...scheme };
        }
      }

      // Use .set() instead of .update() to force signal change detection
      this.schemesSignal.set(newSchemes);
    }

    // backwards compatibility with product recs component skeleton loaders
    if (certonaRecommendations && certonaRecommendations.source !== 'skeleton') {
      this.isInitialized.set(true);
    }
  }

  /**
   * Clear all recommendations
   */
  clearRecommendations(): void {
    this.certonaRecommendationsSubject.next(null);
    this.schemesSignal.set({});
    this.isInitialized.set(false);
  }

  /**
   * Bind the recommendations handler through the Monetate handler repository.
   * @param customHandler Optional custom handler (e.g. PDP's getCertonaRecommendations). When
   * provided, the custom handler is registered instead of the default recommendations() binding,
   * allowing consumers to supply their own processing logic while still using the supported
   * AddMonetateResponseHandler registration path. PLP/Cart callers that pass no argument continue
   * to use the default recommendations() binding unchanged.
   */
  bindRecommendationsHandler(
    customHandler?: (certonaRecommendations: CertonaRecommendations) => unknown,
  ): void {
    if (customHandler) {
      // Re-bind to the latest custom handler (e.g. new PDP component instance on client-side
      // PDP -> PDP navigation)
      this.monetateRecommendationsHandler = customHandler;
    } else if (!this.monetateRecommendationsHandler) {
      this.monetateRecommendationsHandler = (certonaRecommendations: CertonaRecommendations) =>
        this.recommendations(certonaRecommendations);
    }

    const dsgTag = this.window._dsgTag;
    const monetateResponseHandlers: ((data: CertonaRecommendations) => unknown)[] | undefined =
      dsgTag?.monetateResponseHandlers;

    if (
      dsgTag?.FunctionRepository?.AddMonetateResponseHandler &&
      Array.isArray(monetateResponseHandlers) &&
      !monetateResponseHandlers.includes(this.monetateRecommendationsHandler)
    ) {
      dsgTag.FunctionRepository.AddMonetateResponseHandler(this.monetateRecommendationsHandler);
    }
  }
}
